import { Component,  ChangeDetectorRef, inject } from '@angular/core';
import { EnvioCorreoMasivoService, DetalleEnvio, EnvioCorreoMasivoRequest } from './envioCorreoMasivo.service';
import { AlertService } from '../../commons/components/alert/alert.service';
import { AlertComponent } from "../../commons/components/alert/alert.component";
import { AppModule } from '../../app-module';
import { Workbook } from 'exceljs';
import * as FileSaver from 'file-saver';

@Component({
  selector: 'oaj-envio-correo-masivo',
  templateUrl: 'envioCorreoMasivo.component.html',
  styleUrls: ['envioCorreoMasivo.component.css'],
  imports: [AppModule, AlertComponent],
  providers: [EnvioCorreoMasivoService, AlertService]
})
export class EnvioCorreoMasivoComponent {

  loading = false;
  
  // Captura de identificadores
  nuevoIdOrganismo: number | null = null;
  idsOrganismos: number[] = [];
  
  // Resultados del envío
  resultadosEnvio: DetalleEnvio[] = [];
  totalProcesados = 0;
  totalEnviados = 0;
  totalErrores = 0;
  
  // Control de vistas
  mostrarResultados = false;
  
  // Filtros y búsqueda
  searchText = '';

  private alertService: AlertService = inject(AlertService);
  private service: EnvioCorreoMasivoService = inject(EnvioCorreoMasivoService);
  private changeDetectorRef: ChangeDetectorRef = inject(ChangeDetectorRef);

  

  /**
   * Agrega un identificador de organismo a la lista
   */
  agregarIdOrganismo(): void {
    if (!this.nuevoIdOrganismo) {
      setTimeout(() => {
        this.alertService.warn('Debe ingresar un identificador de organismo', { autoClose: true, keepAfterRouteChange: false });
      }, 0);
      return;
    }

    if (this.nuevoIdOrganismo <= 0) {
      setTimeout(() => {
        this.alertService.warn('El identificador debe ser un número positivo', { autoClose: true, keepAfterRouteChange: false });
      }, 0);
      return;
    }

    if (this.idsOrganismos.includes(this.nuevoIdOrganismo)) {
      setTimeout(() => {
        this.alertService.warn('Este identificador ya está en la lista', { autoClose: true, keepAfterRouteChange: false });
      }, 0);
      return;
    }

    this.idsOrganismos.push(this.nuevoIdOrganismo);
    this.nuevoIdOrganismo = null;
    this.changeDetectorRef.detectChanges();
  }

  /**
   * Elimina un identificador de organismo de la lista
   */
  eliminarIdOrganismo(id: number): void {
    this.idsOrganismos = this.idsOrganismos.filter(i => i !== id);
    this.changeDetectorRef.detectChanges();
  }

  /**
   * Limpia toda la lista de identificadores
   */
  limpiarLista(): void {
    this.idsOrganismos = [];
    this.nuevoIdOrganismo = null;
    this.changeDetectorRef.detectChanges();
  }

  /**
   * Abre el selector de archivos Excel
   */
  cargarDesdeExcel(): void {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.xlsx, .xls';
    input.onchange = (event: any) => {
      const file = event.target.files[0];
      if (file) {
        this.procesarArchivoExcel(file);
      }
    };
    input.click();
  }

  /**
   * Procesa el archivo Excel para extraer los IDs de organismos
   */
  private procesarArchivoExcel(file: File): void {
    this.loading = true;
    const reader = new FileReader();

    reader.onload = (e: any) => {
      try {
        const workbook = new Workbook();
        workbook.xlsx.load(e.target.result).then(() => {
          const worksheet = workbook.worksheets[0];
          
          if (!worksheet) {
            this.loading = false;
            this.changeDetectorRef.detectChanges();
            setTimeout(() => {
              this.alertService.error('El archivo Excel está vacío', { autoClose: true, keepAfterRouteChange: false });
            }, 0);
            return;
          }

          const idsExtraidos: number[] = [];
          let filaConError = 0;

          // Leer desde la fila 2 (asumiendo que la fila 1 es encabezado)
          worksheet.eachRow((row, rowNumber) => {
            if (rowNumber === 1) return; // Saltar encabezado

            // Leer la primera columna (columna A)
            const valor = row.getCell(1).value;
            
            if (valor !== null && valor !== undefined && valor !== '') {
              const id = Number(valor);
              
              if (!isNaN(id) && id > 0) {
                if (!idsExtraidos.includes(id) && !this.idsOrganismos.includes(id)) {
                  idsExtraidos.push(id);
                }
              } else if (filaConError === 0) {
                filaConError = rowNumber;
              }
            }
          });

          if (idsExtraidos.length === 0) {
            this.loading = false;
            this.changeDetectorRef.detectChanges();
            setTimeout(() => {
              this.alertService.warn('No se encontraron identificadores válidos en el archivo Excel', { autoClose: true, keepAfterRouteChange: false });
            }, 0);
            return;
          }

          // Agregar los IDs extraídos a la lista
          this.idsOrganismos = [...this.idsOrganismos, ...idsExtraidos];
          
          let mensaje = `Se agregaron ${idsExtraidos.length} identificador(es) desde el archivo Excel`;
          if (filaConError > 0) {
            mensaje += `. Nota: Se encontraron valores no válidos a partir de la fila ${filaConError}`;
          }
          
          this.loading = false;
          this.changeDetectorRef.detectChanges();
          setTimeout(() => {
            this.alertService.success(mensaje, { autoClose: true, keepAfterRouteChange: false });
          }, 0);
        }).catch((error) => {
          console.error('Error al procesar Excel:', error);
          this.loading = false;
          this.changeDetectorRef.detectChanges();
          setTimeout(() => {
            this.alertService.error('Error al procesar el archivo Excel. Verifique que el formato sea correcto.', { autoClose: true, keepAfterRouteChange: false });
          }, 0);
        });
      } catch (error) {
        console.error('Error al leer Excel:', error);
        this.loading = false;
        this.changeDetectorRef.detectChanges();
        setTimeout(() => {
          this.alertService.error('Error al leer el archivo Excel', { autoClose: true, keepAfterRouteChange: false });
        }, 0);
      }
    };

    reader.onerror = () => {
      this.loading = false;
      this.changeDetectorRef.detectChanges();
      setTimeout(() => {
        this.alertService.error('Error al leer el archivo', { autoClose: true, keepAfterRouteChange: false });
      }, 0);
    };

    reader.readAsArrayBuffer(file);
  }

  /**
   * Envía los correos masivos a los organismos capturados
   */
  enviarCorreosMasivos(): void {
    if (this.idsOrganismos.length === 0) {
      setTimeout(() => {
        this.alertService.warn('Debe agregar al menos un identificador de organismo para enviar correos', { autoClose: true, keepAfterRouteChange: false });
      }, 0);
      return;
    }

    this.loading = true;
    
    const request: EnvioCorreoMasivoRequest = {
      idsOrganismos: this.idsOrganismos
    };

    this.service.enviarCorreosMasivos(request).subscribe({
      next: (response) => {
        if (response.succcess) {
          this.resultadosEnvio = response.data.detalles;
          this.totalProcesados = response.data.totalProcesados;
          this.totalEnviados = response.data.totalEnviados;
          this.totalErrores = response.data.totalErrores;
          this.mostrarResultados = true;
          this.loading = false;
          this.changeDetectorRef.detectChanges();
          setTimeout(() => {
            this.alertService.success(response.message, { autoClose: true, keepAfterRouteChange: false });
          }, 0);
        } else {
          this.loading = false;
          this.changeDetectorRef.detectChanges();
          setTimeout(() => {
            this.alertService.error(response.message, { autoClose: true, keepAfterRouteChange: false });
          }, 0);
        }
      },
      error: (error) => {
        console.error(JSON.stringify(error));
        this.loading = false;
        this.changeDetectorRef.detectChanges();
        setTimeout(() => {
          this.alertService.error('Error al enviar los correos masivos', { autoClose: true, keepAfterRouteChange: false });
        }, 0);
      }
    });
  }

  /**
   * Exporta los resultados a un archivo Excel
   */
  exportarResultadosExcel(): void {
    if (this.resultadosEnvio.length === 0) {
      setTimeout(() => {
        this.alertService.warn('No hay resultados para exportar', { autoClose: true, keepAfterRouteChange: false });
      }, 0);
      return;
    }

    const workbook = new Workbook();
    const worksheet = workbook.addWorksheet('Reporte de Envío Masivo');

    // Título del reporte
    const valores = [];
    valores[1] = 'Reporte de Envío Masivo de Correos';
    const rowTitulo = worksheet.addRow(valores);
    
    rowTitulo.eachCell((cell) => {
      cell.font = { size: 18, bold: true, color: { argb: 'FFFFFF' } };
      cell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: '5B4E97' }
      };
    });

    worksheet.mergeCells('A1:F1');

    // Resumen
    const fechaActual = new Date().toLocaleDateString('es-MX', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
    
    worksheet.addRow([]);
    worksheet.addRow(['Fecha:', fechaActual]);
    worksheet.addRow(['Total Procesados:', this.totalProcesados]);
    worksheet.addRow(['Total Enviados:', this.totalEnviados]);
    worksheet.addRow(['Total Errores:', this.totalErrores]);
    worksheet.addRow([]);

    // Configuración de columnas
    worksheet.columns = [
      { key: 'A', width: 15 },
      { key: 'B', width: 50 },
      { key: 'C', width: 35 },
      { key: 'D', width: 15 },
      { key: 'E', width: 60 }
    ];

    // Encabezados de la tabla
    const headers = ['ID Organismo', 'Nombre Organismo', 'Correo', 'Estado', 'Mensaje Error'];
    const rowHeaders = worksheet.addRow(headers);
    
    rowHeaders.eachCell((cell) => {
      cell.font = { bold: true, color: { argb: 'FFFFFF' } };
      cell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: '00008B' }
      };
      cell.alignment = { horizontal: 'center', vertical: 'middle' };
    });

    // Datos de los resultados
    this.resultadosEnvio.forEach((item) => {
      const temp = [
        item.idOrganismo,
        item.nombreOrganismo,
        item.correo,
        item.enviado ? 'Enviado' : 'Error',
        item.mensajeError || 'N/A'
      ];

      const rowTemp = worksheet.addRow(temp);
      
      rowTemp.eachCell({ includeEmpty: true }, (cell, colNumber) => {
        cell.border = {
          top: { style: 'thin' },
          left: { style: 'thin' },
          bottom: { style: 'thin' },
          right: { style: 'thin' }
        };

        // Colorear la celda de estado según el resultado
        if (colNumber === 4) {
          if (item.enviado) {
            cell.fill = {
              type: 'pattern',
              pattern: 'solid',
              fgColor: { argb: 'C6EFCE' }
            };
            cell.font = { color: { argb: '006100' } };
          } else {
            cell.fill = {
              type: 'pattern',
              pattern: 'solid',
              fgColor: { argb: 'FFC7CE' }
            };
            cell.font = { color: { argb: '9C0006' } };
          }
        }
      });
    });

    // Guardar el archivo
    workbook.xlsx.writeBuffer().then((data) => {
      const blob = new Blob([data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const nombreArchivo = `ReporteEnvioMasivo_${new Date().getTime()}.xlsx`;
      FileSaver.saveAs(blob, nombreArchivo);
      setTimeout(() => {
        this.alertService.success('Reporte exportado exitosamente', { autoClose: true, keepAfterRouteChange: false });
      }, 0);
    }).catch((error) => {
      console.error('Error al generar el archivo Excel:', error);
      setTimeout(() => {
        this.alertService.error('Error al generar el archivo Excel', { autoClose: true, keepAfterRouteChange: false });
      }, 0);
    });
  }

  /**
   * Reinicia el formulario y los resultados
   */
  resetear(): void {
    this.idsOrganismos = [];
    this.nuevoIdOrganismo = null;
    this.resultadosEnvio = [];
    this.totalProcesados = 0;
    this.totalEnviados = 0;
    this.totalErrores = 0;
    this.mostrarResultados = false;
    this.changeDetectorRef.detectChanges();
  }

  /**
   * Filtra los IDs según el texto de búsqueda
   */
  get idsFiltrados(): number[] {
    if (!this.searchText) {
      return this.idsOrganismos;
    }
    
    return this.idsOrganismos.filter(id => 
      id.toString().includes(this.searchText)
    );
  }

  /**
   * Filtra los resultados según el texto de búsqueda
   */
  get resultadosFiltrados(): DetalleEnvio[] {
    if (!this.searchText) {
      return this.resultadosEnvio;
    }
    
    const searchLower = this.searchText.toLowerCase();
    return this.resultadosEnvio.filter(r => 
      r.nombreOrganismo?.toLowerCase().includes(searchLower) ||
      r.correo?.toLowerCase().includes(searchLower) ||
      r.idOrganismo?.toString().includes(searchLower)
    );
  }
}
